import { useMemo } from 'react';
import { CarVariant, ChargeTest, RangeTest } from '@appTypes/carModelType';

export type ValueObject = { valueArray?: number[]; unit: string; withPrefix?: boolean };

const stringifyValue = (value: number | undefined, withPrefix?: boolean) => {
  if (value === undefined) return undefined;
  if (withPrefix && value >= 1000000) return `${parseFloat((value / 1000000).toFixed(1)).toLocaleString('nb-NO')}m`;
  if (withPrefix && value >= 1000) return `${parseFloat((value / 1000).toFixed(0)).toLocaleString('nb-NO')}k`;
  return value.toLocaleString('nb-NO');
};

export const getValueString = ({ valueArray, unit, withPrefix }: ValueObject) => {
  const min = valueArray?.length ? Math.min(...valueArray) : undefined;
  const max = valueArray?.length ? Math.max(...valueArray) : undefined;
  const from = stringifyValue(min, withPrefix);
  const to = stringifyValue(max, withPrefix);
  if (from && to) return from === to ? `${from} ${unit}` : `${from}\u2014${to} ${unit}`;
  if (from) return `Fra ${from} ${unit}`;
  if (to) return `Inntil ${to} ${unit}`;
  return 'Ikke oppgitt';
};

export const useVariantData = (variants: CarVariant[], rangeTests?: RangeTest[], chargeTests?: ChargeTest[]) =>
  useMemo(
    () => ({
      prices: variants.filter((variant) => variant?.priceNOK).map((variant) => variant.priceNOK),
      consumption:
        rangeTests
          ?.filter((test) => test?.statedConsumptionKWhper100km)
          .map((test) => test.statedConsumptionKWhper100km) ?? [],
      ranges: rangeTests?.filter((test) => test?.statedRangeKm).map((test) => test.statedRangeKm) ?? [],
      payloads: variants
        .filter((variant) => variant?.vehicleDimensions?.maxPayloadkg)
        .map((variant) => variant.vehicleDimensions.maxPayloadkg),
      hasTrailer: variants.some((variant) => variant?.trailerDetails?.trailerHitch === 'Ja'),
      trailerPayloads: variants
        .filter((variant) => variant?.trailerDetails?.trailerMaximumWeight_kg)
        .map((variant) => variant.trailerDetails.trailerMaximumWeight_kg),
      cargospace: variants
        .filter((variant) => variant?.vehicleDimensions?.cargospace_l)
        .map((variant) => variant.vehicleDimensions.cargospace_l),
      driveTrain: [
        ...new Set(variants.filter((variant) => variant?.driveTrain?.type).map((variant) => variant.driveTrain.type)),
      ],
      chargeSpeed:
        chargeTests?.filter((test) => test?.maxChargeEfficiency).map((test) => test.maxChargeEfficiency) ?? [],
    }),
    [variants, rangeTests, chargeTests],
  );
